import Missing from "../../../Missing";
import Header from "../../../../components/common/Header";
import Icon from "../../../../components/common/Icon";
import useDeceased from "../../../../hooks/useDeceased";
import {getDeceasedInfos} from "../../../../api/pro/deceasedReq";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import useAuth from "../../../../hooks/useAuth";
import {useEffect, useRef, useState} from "react";
import {
    deleteFprFileFromDb,
    getFprContentList,
    getFprDecesIntervenantList,
    getFprFile,
    getFprPrint,
    getFprRemerciementDataSaved,
    getFprRemerciementTemplateList,
    getRemerciementTextList,
    saveFprData,
    saveTemplate, uploadFprPdf
} from "../../../../api/pro/fprReq";
import FprBreadCrumbs from "./FprBreadCrumbs";
import {PDFViewer} from "@react-pdf/renderer";
import {fileToBase64, getErrorListFromValidation, getFprEventDefaultContent, processFprTextEquivalences, removeErrorList} from "../../../../helper/tools";
import {safeJwtDecode} from "../../../../helper/jwtHelper";
import FormEditFprContent from "../../../../forms/deceased/FormEditFprContent";

export default function FairePartRemerciement({ tab : initialTab }) {
    const { deceasedInfos, setDeceasedInfos } = useDeceased();
    const { id } = useParams();
    const { auth } = useAuth();
    const token = auth.token;
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [ fprTemplateList, setFprTemplateList ] = useState();
    const decodedToken = safeJwtDecode(token);
    const agencyId = decodedToken?.agency;
    const IMG_FOLDER = process.env.REACT_APP_LOCAL_ASSETS_BASE_URL;
    const [ fprDataSaved, setFprDataSaved ] = useState();
    const [ fprDataContentSaved, setFprDataContentSaved ] = useState();
    const [ showEditContentThkModal, setShowEditContentThkModal ] = useState(false);
    const [ contentType, setContentType ] = useState();
    const [ selectedTemplate, setSelectedTemplate ] = useState();
    const [ tab, setTab ] = useState(initialTab);
    const [ savedTemplate, setSavedTemplate ] = useState();
    const [ fprFile, setFprFile ] = useState();
    const [ fprPdfFile, setFprPdfFile ] = useState();
    const [ uploadSuccess, setUploadSuccess ] = useState(false);
    const [ apercuPdfUrl, setApercuPdfUrl ] = useState();
    const [ apercuLoading, setApercuLoading ] = useState(false);

    const apiDeceasedInfos = async () => {
        const response = await getDeceasedInfos(token, id)
        setDeceasedInfos(response)
    }

    const apiFprFile = async () => {
        const response = await getFprFile(token, id, "2")
        if (response?.error) {
            return;
        } else {
            setFprFile(response)
        }
    }

    // Correspondance entre le "contentType" envoyé à saveFprData et la clé sous laquelle
    // la rubrique est stockée dans fprDataContentSaved (voir FPRTemplateController::saveFprData).
    const fprContentTypeToKey = {
        "epitaphes": "form-modalEpitaphe",
        "intervenants": "form-modalIntervenant",
        "formule de remerciement": "form-modalIntro",
        "adresse de correspondance": "form-modalAdressCorres",
    };
    const isFprContentAlreadyFilled = (contentType) => !!fprDataContentSaved?.[fprContentTypeToKey[contentType]];

    // Pré-remplit chaque rubrique avec la première formule de sa liste, pour ne pas arriver
    // sur un faire-part totalement vide dès le choix du thème (même logique que pour les
    // faire-part de décès, voir FairePartDeces::prefillDefaultFprContent).
    const prefillDefaultFprContent = async () => {
        const firstOf = (list) => (Array.isArray(list) && list.length > 0) ? list[0] : null;
        const sections = [];

        if (!isFprContentAlreadyFilled("epitaphes")) {
            const epitapheList = await getFprContentList(token, 1);
            const firstEpitaphe = firstOf(processFprTextEquivalences(Array.isArray(epitapheList) ? epitapheList : [], deceasedInfos));
            if (firstEpitaphe?.text) {
                sections.push({ contentType: "epitaphes", content: firstEpitaphe.text });
            }
        }

        if (!isFprContentAlreadyFilled("intervenants")) {
            const intervenantList = await getFprDecesIntervenantList(token, id);
            const firstIntervenant = firstOf(intervenantList);
            if (firstIntervenant?.text) {
                sections.push({ contentType: "intervenants", content: firstIntervenant.text });
            }
        }

        if (!isFprContentAlreadyFilled("formule de remerciement")) {
            const remerciementList = await getRemerciementTextList(token, 0, 2, id);
            const firstRemerciement = firstOf(remerciementList);
            if (firstRemerciement?.text) {
                sections.push({ contentType: "formule de remerciement", content: firstRemerciement.text });
            }
        }

        if (!isFprContentAlreadyFilled("adresse de correspondance")) {
            const addressContent = getFprEventDefaultContent('Adresse de correspondance', { eventList: [], deceasedInfos, deceasedEventInfos: null });
            if (addressContent) {
                sections.push({ contentType: "adresse de correspondance", content: addressContent });
            }
        }

        for (const section of sections) {
            await saveFprData(token, {
                deceasedId: id,
                typeFpr: "2",
                content: section.content,
                contentType: section.contentType,
            });
        }

        if (sections.length > 0) {
            apiDeceasedFprDataSaved();
        }
    }

    const handleChangeFormat = async (template) => {
        if (id && template?.id) {
            await saveTemplate(token, {
                deceasedId: id,
                typeFpr: 2,
                templateId: template.id
            })
        }
        setSelectedTemplate(template)

        // Pré-remplir dès le clic sur un thème, avant même de valider "Suivant", afin que
        // l'aperçu affiché montre un vrai contenu. Se relance à chaque changement de template :
        // prefillDefaultFprContent ne complète que les rubriques encore vides.
        prefillDefaultFprContent()
    }

    const apiDeceasedFprDataSaved = async () => {
        const response = await getFprRemerciementDataSaved(token, id)
        setFprDataContentSaved(response['dataSaved'])
        setFprDataSaved(response)
    }

    const apiFprTemplateList = async () => {
        const response = await getFprRemerciementTemplateList(token)
        setFprTemplateList(response)
    }

    useEffect(() => {
        if (fprDataContentSaved) {
            const fprTemplate = fprTemplateList?.find(template => template.id == fprDataContentSaved?.['form-template'])
            setSelectedTemplate(fprTemplate)

            // Complète les rubriques encore vides d'un faire-part déjà existant (ex: créé avant
            // l'ajout du pré-remplissage automatique) sans attendre un nouveau clic sur un thème.
            // fprTemplateList est dans les dépendances : apiDeceasedFprDataSaved et
            // apiFprTemplateList sont deux appels indépendants qui peuvent résoudre dans
            // n'importe quel ordre, sans ça une résolution de la liste après les données déjà
            // enregistrées ne relançait jamais ce pré-remplissage (fprTemplate introuvable).
            if (fprTemplate) {
                prefillDefaultFprContent()
            }
        }
    }, [ fprDataContentSaved, fprTemplateList ]);

    // Aperçu fidèle à l'impression : on récupère le même PDF que le bouton "Imprimer"
    // au lieu de reconstruire la mise en page en HTML/CSS.
    // Ce useRef sert de garde-fou contre les réponses obsolètes : à la sélection d'un template,
    // une génération démarre avant même que le pré-remplissage (épitaphe, intervenants, formule,
    // adresse) n'ait fini d'enregistrer ses données, puis une seconde démarre une fois le contenu
    // sauvegardé. Sans garde-fou, si la première requête répond après la seconde, elle écrase
    // l'aperçu à jour avec une version incomplète (ex: sans l'adresse de correspondance).
    const apercuRequestIdRef = useRef(0);
    const apiApercuPdf = () => {
        if (!id) return;
        const requestId = ++apercuRequestIdRef.current;
        setApercuLoading(true);
        getFprPrint(token, id, 2)
            .then(response => {
                if (requestId !== apercuRequestIdRef.current) return;
                if (response instanceof Blob && response.type === 'text/html') {
                    response.text().then(downloadUrl => {
                        if (requestId !== apercuRequestIdRef.current) return;
                        setApercuPdfUrl(`${downloadUrl}?t=${Date.now()}`);
                        setApercuLoading(false);
                    })
                } else {
                    setApercuLoading(false);
                }
            })
            .catch(() => {
                if (requestId === apercuRequestIdRef.current) setApercuLoading(false);
            });
    }

    useEffect(() => {
        if (selectedTemplate) {
            apiApercuPdf()
        }
    }, [ fprDataContentSaved, selectedTemplate ]);

    useEffect(() => {
        apiFprTemplateList()
    }, [ token ]);

    useEffect(() => {
        apiDeceasedFprDataSaved()
        apiDeceasedInfos()
        apiFprFile()
    }, [ id ]);

useEffect(() => {
        apiFprFile()
    }, [ fprPdfFile ]);

    const handleClickButton = (e) => {
        let myContentType = e.target.nextSibling.textContent
        setContentType(myContentType)
        setShowEditContentThkModal(true)
    }

    const handlePrintFpr = () => {
        if (id) {
            getFprPrint(token, id, 2)
                .then(response => {
                    if (response instanceof Blob && response.type === 'text/html') {
                        response.text().then(downloadUrl => {
                            const pdfLink = document.createElement('a');
                            pdfLink.href = downloadUrl;
                            pdfLink.target = '_blank';
                            pdfLink.setAttribute('download', `fpr_remerciement_${deceasedInfos?.lastName}_${deceasedInfos?.firstName}.pdf`);
                            document.body.appendChild(pdfLink);
                            pdfLink.click();
                            document.body.removeChild(pdfLink);
                        })
                    }
                })
        }
    }

    const handleChangeFprFile = async (e) => {
        let fprFile = e.target.files[0]
        if (e) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setFprPdfFile(reader.result)
            }
            reader.readAsDataURL(fprFile)
            try {
                const base64FprFile = await fileToBase64(fprFile);
                setFprPdfFile(base64FprFile)
            } catch (error) {
                console.error("Erreur lors de la conversion en base64 : ", error);
            }
        }
    }

    const apiUploadedFile = async() => {
        if (fprPdfFile) {
            let fileName = "fprPdf.pdf";
            let pdfId = fprFile?.id ? fprFile?.id : 0;
            const formData = new FormData();
            formData.append('file', fprPdfFile);
            formData.append('file_name', fileName);
            formData.append('deceasedId', id);
            formData.append('agencyId', agencyId);
            formData.append('id', pdfId);
            formData.append('type', "2");
            const response = await uploadFprPdf(token, formData)
                .then((response) => {
                    removeErrorList();
                    if (response?.response?.data?.error) {
                        getErrorListFromValidation(response?.response?.data?.error)
                        return false;
                    }
                    return true;
                })
            return response;
        }
    };

    const handleUploadFilePdf = async () => {
        if (fprPdfFile) {
            const success = await apiUploadedFile()
            if (success) {
                setUploadSuccess(true)
                await apiFprFile()
            }
        }
    }

    const handleValidateFprPdf = () => {
        navigate(`/defunt/${id}`)
    }

    const handleDeleteFprFile = async () => {
        if (fprFile?.id) {
            const response = await deleteFprFileFromDb(token, fprFile?.id)
            if (!response?.response?.data?.error) {
                setFprFile(null)
                setFprPdfFile(null)
            }
        }
    }

    // zoom=100 pour afficher la page à taille réelle, sans recadrage automatique.
    const getApercuUrl = () => {
        if (!apercuPdfUrl) return apercuPdfUrl;
        return `${apercuPdfUrl}#zoom=100`;
    }

    const handleMNext = () => {
        let body = {
            deceasedId: id,
            typeFpr: 2,
            templateId: selectedTemplate?.id
        }
        saveTemplate(token, body)
            .then(() => {
                setSavedTemplate(fprTemplateList?.find(template => template.id === selectedTemplate?.id))
                setTab("donnees")
                apiFprFile()
                apiDeceasedFprDataSaved()
            })
    }

    const handleMPrev = () => {
        if (tab === "modifier") {
            navigate(`/defunt/${id}`)
        }
    }

    const handleBack = () => {
        navigate(`/defunt/${id}`)
    }

    const handleDNext = () => {
        if (savedTemplate) {
            setTab("finaliser")
        } else {
            setTab("modifier")
        }
    }

    const handleDPrev = () => {
        setTab("modifier")
    }

    const handleFPrev = () => {
        if (savedTemplate) {
            setTab("donnees")
        } else {
            setTab("modifier")
        }
    }

    return (
        <main>
            {fprTemplateList?.error &&
                <Missing />
            }
            { !fprTemplateList?.error &&
                <>
                    <Header
                        location="Espace de gestion des faire-part de remerciement pour "
                        locationComp={`${deceasedInfos?.lastName} ${deceasedInfos?.firstName}`}
                        parent="Défunt"
                        slug={`/defunt/${deceasedInfos?.id}`}
                    />
                    { showEditContentThkModal &&
                        <div className="modal edit-content">
                            <FormEditFprContent
                                typeFpr="2"
                                fprDataContentSaved={fprDataContentSaved}
                                apiFprDataSaved={apiDeceasedFprDataSaved}
                                selectedTemplate={selectedTemplate}
                                setShowModal={setShowEditContentThkModal}
                                contentType={contentType}
                                deceasedInfos={deceasedInfos}
                            />
                        </div>
                    }
                        { !pathname?.includes("voir-fichier") &&
                            <>
                                {!pathname.includes("fichier") && <FprBreadCrumbs fprDataSaved={fprDataSaved} tab={tab} setTab={setTab} selectedTemplate={savedTemplate} location="fpr-deces" />}
                                <div className="double-fieldset">
                                    { tab === "modifier" &&
                                        <fieldset className="fpr-templates">
                                            <h3>Choisissez le thème de votre faire-part</h3>
                                            <div className="flex evenly">
                                                {fprTemplateList?.map((template, index) => (
                                                    <div
                                                        key={index}
                                                        className={`fpr-template-card ${selectedTemplate?.id === template.id ? 'selected' : ''}`}
                                                        onClick={() => handleChangeFormat(template)}
                                                    >
                                                        <div className="fpr-template-label">
                                                            <span>{template.name}</span>
                                                        </div>
                                                        <div className="fpr-template-preview">
                                                            <img className="miniature" src={`${IMG_FOLDER}/img/fpr/symboles-miniature/${template.nameminiaturefile}`} alt={template.title} />
                                                        </div>
                                                        {selectedTemplate?.id === template.id && (
                                                            <div className="fpr-template-selected-badge">
                                                                <i className="fa fa-check-circle"></i>
                                                            </div>
                                                        )}
                                                    </div>

                                                ))}
                                            </div>
                                            <div className="fpr-buttons">
                                                <div className="buttons-container">
                                                    <button type="button" className="fpr-back-to-deceased-button" onClick={handleBack}><i
                                                        className="fa fa-arrow-left"></i><span>Retour</span>
                                                    </button>
                                                    {tab != "modifier" && <button type="button" className="red-button" onClick={handleMPrev}><i
                                                        className="fa fa-times"></i><span>Etape précédente</span>
                                                    </button>}
                                                    { selectedTemplate && <button type="button" className="long green-button" onClick={handleMNext}><i
                                                        className="fa fa-check"></i><span>Valider et passer à l'étape suivante</span>
                                                    </button>}
                                                </div>
                                            </div>
                                        </fieldset>
                                    }
                                    {tab === "donnees" &&
                                        <fieldset className="fpr-templates">
                                            <div className="fpr-buttons">
                                                <div id="button-1"
                                                     className="flex">{fprDataContentSaved?.['form-modalEpitaphe'] ?
                                                    <button onClick={handleClickButton} className="blue-button"><span>Modifier</span></button> :
                                                    <button onClick={handleClickButton}
                                                            className="green-button"><span>Créer</span></button>}<span>Epitaphes</span></div>
                                                <div id="button-2"
                                                     className="flex">{fprDataContentSaved?.['form-modalIntervenant'] ?
                                                    <button onClick={handleClickButton} className="blue-button"><span>Modifier</span></button> :
                                                    <button onClick={handleClickButton}
                                                            className="green-button"><span>Créer</span></button>}<span>Intervenants</span></div>
                                                <div id="button-3"
                                                     className="flex">{fprDataContentSaved?.['form-modalIntro'] ?
                                                    <button onClick={handleClickButton} className="blue-button"><span>Modifier</span></button> :
                                                    <button onClick={handleClickButton}
                                                            className="green-button"><span>Créer</span></button>}<span>Formule de remerciement</span>
                                                </div>
                                                <div id="button-8"
                                                     className="flex">{fprDataContentSaved?.['form-modalAdressCorres'] ?
                                                    <button onClick={handleClickButton} className="blue-button"><span>Modifier</span></button> :
                                                    <button onClick={handleClickButton}
                                                            className="green-button"><span>Créer</span></button>}<span>Adresse de correspondance</span>
                                                </div>
                                            </div>
                                            <div className="fpr-buttons">
                                                <div className="buttons-container">
                                                    <button type="button" className="fpr-back-to-deceased-button" onClick={handleBack}><i
                                                        className="fa fa-arrow-left"></i><span>Retour</span>
                                                    </button>
                                                    <button type="button" className="red-button" onClick={handleDPrev}><i
                                                        className="fa fa-times"></i><span>Etape précédente</span>
                                                    </button>
                                                    <button type="button" className="long green-button" onClick={handleDNext}><i
                                                        className="fa fa-check"></i><span>Valider et passer à l'étape suivante</span>
                                                    </button>
                                                </div>
                                            </div>
                                        </fieldset>
                                    }
                                    {tab === "finaliser" &&
                                        <>
                                            <fieldset>
                                                <p> Vous pouvez imprimer ce faire-part directement dans
                                                    votre agence de Pompes Funèbres ou faire une demande
                                                    de devis pour le faire imprimer dans les règles de l'art
                                                    par des professionnels de l'impression
                                                </p>
                                                <div className="fpr-buttons">
                                                    <div className="buttons-container">
                                                        <button type="button" className="red-button" onClick={handleFPrev}>
                                                            <i className="fa fa-close"></i><span>Retour</span>
                                                        </button>
                                                        <button onClick={handlePrintFpr} className="green-button" type="button"><i className="fa fa-print"/><span>Imprimer</span></button>
                                                    </div>
                                                </div>
                                            </fieldset>
                                        </>
                                    }
                                    { tab === "fichier" &&
                                        <>
                                            <fieldset>
                                                <p>Vous pouvez télécharger le fichier PDF de ce faire-part</p>
                                                { fprFile?.id &&
                                                    <div className="flex-buttons">
                                                        <button onClick={handleDeleteFprFile} className="red-button" type="button" >
                                                            <i className="fa fa-trash"/><span>Supprimer</span></button>
                                                    </div>
                                                }
                                                <div className="flex-buttons">
                                                    <input type="file" name="fprFile" id="fprFile" accept="application/pdf" onChange={handleChangeFprFile} className="hidden"/>
                                                    <label htmlFor="fprFile" className="blue-button">
                                                        <i className="fa fa-folder-open"/>
                                                        <span>{fprPdfFile ? "Fichier sélectionné" : "Choisir un fichier"}</span>
                                                    </label>
                                                    <button className="green-button" name="fprPdf" type="button" onClick={handleUploadFilePdf}><i className="fa fa-download"/><span>Télécharger</span></button>
                                                </div>
                                                {uploadSuccess && (
                                                    <div className="flex fpr-validate-row">
                                                        <button className="blue-button" type="button" onClick={handleValidateFprPdf}>
                                                            <i className="fa fa-check"></i> <span>Valider</span>
                                                        </button>
                                                    </div>
                                                )}
                                            </fieldset>
                                            <fieldset className="fpr-templates">
                                                <h3>Aperçu</h3>
                                                <div className="form-input html-textarea fpr-preview-frame">
                                                    {fprPdfFile ?
                                                        <PDFViewer
                                                            width="100%"
                                                            height="100%"
                                                            src={fprPdfFile}
                                                        />: (fprFile?.id) ?
                                                            <PDFViewer
                                                                width="100%"
                                                                height="100%"
                                                                src={`${IMG_FOLDER}/media/announcement/${agencyId}/${fprFile?.['name']}`}
                                                            /> :<div className='fpr-preview-default'>
                                                                <p>Choisissez un fichier pour voir l'aperçu</p>
                                                            </div>
                                                    }
                                                </div>
                                            </fieldset>
                                        </>
                                    }
                                    { tab !== "fichier" &&
                                        <fieldset className="fpr-templates">
                                            <h3>Aperçu</h3>
                                            { !selectedTemplate ?
                                                <div className="fpr-preview-default">
                                                    <p>Choisissez un modèle pour voir l'aperçu</p>
                                                </div> :
                                                apercuLoading || !apercuPdfUrl ?
                                                    <div className="fpr-preview-default">
                                                        <p>Génération de l'aperçu...</p>
                                                    </div> :
                                                    <div className="form-input html-textarea fpr-preview-frame">
                                                        <PDFViewer width="100%" height="100%" src={getApercuUrl()}/>
                                                    </div>
                                            }
                                        </fieldset>
                                    }
                                </div>
                                {tab === "fichier" &&
                                    <div className="buttons-container back bottom-centered">
                                        <button type="button" className="fpr-back-to-deceased-button" onClick={handleBack}>
                                            <i className="fa fa-arrow-left"></i><span>Retour</span>
                                        </button>
                                    </div>
                                }
                            </>
                        }
                        { pathname?.includes("voir-fichier") &&
                            <fieldset className="mini">
                                <h3>Aperçu</h3>
                                { !selectedTemplate ?
                                    <div className="fpr-preview-default">
                                        <p>Choisissez un modèle pour voir l'aperçu</p>
                                    </div> :
                                    apercuLoading || !apercuPdfUrl ?
                                        <div className="fpr-preview-default">
                                            <p>Génération de l'aperçu...</p>
                                        </div> :
                                        <div className="form-input html-textarea fpr-preview-frame">
                                            <PDFViewer width="100%" height="100%" src={getApercuUrl()}/>
                                        </div>
                                }
                            </fieldset>
                        }
                </>
            }
        </main>
    );
}